<template>
  <div v-if="shouldShow || forceShow" :class="containerClass" :style="mode === 'fixed' ? 'height: 48px;' : ''">
    <!-- Account Button -->
    <button
      v-if="showAccountButton"
      @click="goToAccount"
      class="flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border border-gray-200 dark:border-gray-700 hover:bg-white dark:bg-gray-900 hover:shadow-lg active:scale-95 transition-all duration-200 shadow-md flex-shrink-0"
      :class="mode === 'fixed' ? 'w-12 h-12 rounded-2xl' : 'w-10 h-10 rounded-xl'"
      :aria-label="$t('Account')"
    >
      <svg class="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    </button>

    <!-- Language Switcher：與 header 按鈕同高、內容垂直居中 -->
    <div
      class="language-switcher relative flex items-stretch"
      :class="{ 'language-switcher--open': showLanguageMenu }"
    >
      <button 
        type="button"
        @click="toggleLanguageMenu"
        class="inline-flex items-center justify-center gap-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border border-gray-200 dark:border-gray-700 px-4 hover:bg-white dark:bg-gray-900 hover:shadow-lg active:scale-95 transition-all duration-200 shadow-md flex-shrink-0"
        :class="mode === 'fixed' ? 'h-12 rounded-2xl' : 'h-10 rounded-xl'"
        aria-label="切换语言"
      >
        <svg class="w-5 h-5 text-gray-600 dark:text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
        <span class="language-current-name text-sm font-semibold text-gray-700 dark:text-gray-300 leading-none align-middle">{{ getCurrentLanguageName() }}</span>
        <svg class="language-current-chevron w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': showLanguageMenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      <!-- Language Dropdown -->
      <div 
        v-if="showLanguageMenu"
        ref="languageMenuRef"
        class="language-menu-dropdown absolute top-full right-0 mt-2 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 py-3 w-max min-w-[11rem] z-[200]"
      >
        <button 
          v-for="lang in availableLanguages" 
          :key="lang.code"
          :data-selected="locale === lang.code ? 'true' : 'false'"
          @click="changeLanguage(lang.code)"
          class="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 active:bg-gray-100 dark:bg-gray-800 dark:active:bg-gray-800 transition-colors flex items-center gap-3 whitespace-nowrap"
          :class="{ 'bg-brand/10 text-brand font-semibold': locale === lang.code }"
          style="min-height: 44px;"
        >
          <span class="text-xl">{{ lang.flag }}</span>
          <span class="font-medium shrink-0">{{ lang.name }}</span>
          <svg v-if="locale === lang.code" class="w-5 h-5 ml-auto shrink-0 text-brand" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  applyAutoLanguage,
  availableLanguages,
  getCurrentLanguageName as getLanguageName,
  setManualLanguagePreference
} from '@/utils/language'

const props = defineProps({
  mode: {
    type: String,
    default: 'fixed', // 'fixed' | 'inline'
    validator: (value) => ['fixed', 'inline'].includes(value)
  },
  forceShow: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const showLanguageMenu = ref(false)
const languageMenuRef = ref(null)

// 定义不显示个人中心按钮的页面
const noAccountButtonRoutes = ['account', 'AccountView', 'LoginView', 'RegisterView', 'ForgotPasswordView', 'le', 'lg']

// 判断是否显示个人中心按钮
const showAccountButton = computed(() => {
  return !noAccountButtonRoutes.includes(route.name)
})

// 判断是否显示整个组件
const shouldShow = computed(() => {
  return !route.meta.hideTopActions
})

const containerClass = computed(() => {
  if (props.mode === 'fixed') {
    return 'top-right-actions top-right-actions--fixed fixed top-4 right-4 z-50 flex items-stretch gap-3'
  }
  return 'flex items-center gap-2'
})

const scrollLanguageMenuToSelection = () => {
  nextTick(() => {
    const menu = languageMenuRef.value
    if (!menu) return
    const selected = menu.querySelector('[data-selected="true"]')
    selected?.scrollIntoView({ block: 'center', inline: 'nearest' })
  })
}

// Language switching functions
const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value
  if (showLanguageMenu.value) {
    scrollLanguageMenuToSelection()
  }
}

const changeLanguage = (langCode) => {
  locale.value = langCode
  showLanguageMenu.value = false
  setManualLanguagePreference(langCode)
}

const getCurrentLanguageName = () => {
  return getLanguageName(locale.value)
}

// Close language menu when clicking outside
const closeLanguageMenu = (event) => {
  if (!event.target.closest('.language-switcher')) {
    showLanguageMenu.value = false
  }
}

// 跳转到个人中心页面
const goToAccount = () => {
  router.push('/account')
}

onMounted(() => {
  applyAutoLanguage(locale)
  window.addEventListener('languagechange', syncAutoLanguage)
  document.addEventListener('click', closeLanguageMenu)
})

onUnmounted(() => {
  // Remove click outside listener
  window.removeEventListener('languagechange', syncAutoLanguage)
  document.removeEventListener('click', closeLanguageMenu)
})

const syncAutoLanguage = () => {
  applyAutoLanguage(locale)
}
</script>

<style scoped>
.top-right-actions {
  /* Ensure it's always on top */
  z-index: 9999;
}

:global(.tg-home-header .mb-4:has(.language-switcher--open)),
:global(.page-header-bar .header-inline-actions:has(.language-switcher--open)) {
  position: relative;
  z-index: 120;
}

.language-menu-dropdown {
  max-height: min(360px, calc(100dvh - 12rem));
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.language-menu-dropdown::-webkit-scrollbar {
  display: none;
}
</style>
